<template>
    <div class="edit-container">
        <h1 class="mt-3 page-title">Modifier un matricule utilisateur</h1>

        <div v-if="isLoading" class="flex justify-content-center">
            <ProgressSpinner aria-label="Chargement..." />
        </div>
        
        <div v-else>
            <div v-if="companyMatricule" class="form-container">
                <button @click="redirectToCompanyMatriculeList(companyMatricule)"
                    class="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 bg-white transition-colors duration-300 hover:bg-blue-500 hover:text-white rounded-md text-sm"
                >
                    <svg class="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour
                </button>

                <div class="mx-auto shadow-md p-6 rounded-lg w-[90%] md:w-[80%] lg:w-[50%]">
                    <div class="justify-center">
                        <FormKit type="form"
                             outer-class="w-full"
                             :actions="false"
                             :model="formData"
                             @submit="onSubmit"
                             #default="{state: { valid }}"
                             incomplete-message="Veuillez remplir tous les champs correctement."
                        >
                            <FormKit type="text"
                                 name="userMatricule"
                                 label="Matricule*"
                                 placeholder="Enter user matricule"
                                 v-model="formData.userMatricule"
                                 validation="required|length:1,25|validateMatricule"
                                 validation-visibility="live"
                                 :validation-rules="{ validateMatricule }"
                                 :validation-messages="{
                                    required: 'Veuillez renseigner le matricule',
                                    length: 'Veuillez renseigner un matricule entre 1 and 25 caractères',
                                    validateMatricule: 'Le matricule ne peut pas contenir d\'espaces, points (.), virgules (,), points-virgules (;) ou underscores (_)'
                                }"
                                 @keydown="preventSpaceOnWriteText"
                            />

                            <FormKit type="checkbox"
                                 name="available"
                                 label="Available"
                                 v-model="formData.available"
                                 :disabled="cannotEditAvailable()"
                            />

                            <FormKit type="autocomplete"
                                 name="companyUuid"
                                 empty-message="Aucun utilisateur"
                                 v-model="formData.selectedUser"
                                 label="Utilisateur"
                                 placeholder="Sélectionner un utilisateur"
                                 selection-appearance="option"
                                 :options="searchUsersWithoutMatriculeByCompany"
                            />

                            <div v-if="companyMatricule.user" class="user-details">
                                <h3>Client lié au matricule</h3>
                                <div class="user-info">
                                    <div class="info-row">
                                        <span class="info-label">Email:</span>
                                        <span class="info-value">{{ companyMatricule.user.email }}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Prénom:</span>
                                        <span class="info-value">{{ companyMatricule.user.firstname }}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Nom:</span>
                                        <span class="info-value">{{ companyMatricule.user.lastname }}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div v-else class="no-user-assigned">
                                <p>Aucun utilisateur n'est affecté à cette matricule.</p>
                            </div>

                            <div class="mt-3">
                                <FormKit type="submit" label="Enregistrer" input-class="btn-primary" :disabled="!valid"/>
                            </div>
                        </FormKit>

                    </div>
                </div>
            </div>

            <div v-else class="form-container error">
                <button @click="redirectToCompanyList()"
                    class="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 bg-white transition-colors duration-300 hover:bg-blue-500 hover:text-white rounded-md text-sm"
                >
                    <svg class="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour
                </button>
                <p>Matricule non trouvé.</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {useCompanyMatriculeStore} from "@/stores/companyMatricule/companyMatriculeStore";
import {fetchCompanyMatricule} from "@/_services/companyMatricule/companyMatricule";
import {getPaginatedUsersWithoutMatriculeByCompanyForDropDown} from "@/_services/user/user";
import ApiService from "@/_services/api.service";
import {IFormkitDropDown} from "@/models/formkit/dropDown/IFormkitDropDown";
import {ref, watch, computed, onMounted, onUnmounted} from "vue";
import {useRoute, onBeforeRouteLeave, onBeforeRouteUpdate} from 'vue-router';
import ProgressSpinner from "primevue/progressspinner";
import router from "@/router";
import swal from "@/_helpers/swal";
import {IUserMatriculeByCompany} from "@/models/companyMatricule/IUserMatriculeByCompany";

const route = useRoute();
const store = useCompanyMatriculeStore();
const isLoading = ref(true);
const companyMatricule = computed(() => store.selectedMatricule);

interface IEditCompanyMatriculeForm {
    userMatricule: string;
    selectedUser?: undefined | string;
    available: boolean;
}

const formData = ref<IEditCompanyMatriculeForm>({
    userMatricule: '',
    selectedUser: undefined,
    available: false,
});

onMounted(async() => {
    const urlMatriculeUuid = route.params.uuid as string;
    if (companyMatricule.value && companyMatricule.value.uuid === urlMatriculeUuid) {
        formData.value.userMatricule = companyMatricule.value.userMatricule;
        formData.value.available = companyMatricule.value.available;
        formData.value.selectedUser = (companyMatricule.value.user)
            ? formData.value.selectedUser = companyMatricule.value.user.uuid
            : undefined;
            
        isLoading.value = false;
    } else {
        await fetchCompanyMatriculeByUuidIfNotInStore(urlMatriculeUuid);
    }
});

onUnmounted(() => {
    store.clearSelectedMatricule();
});

watch(
    companyMatricule,
    (newValue) => {
        if (newValue) {
            formData.value.userMatricule = newValue.userMatricule;
            formData.value.available = newValue.available;
        }
    },
    { immediate: true }
);

onBeforeRouteLeave(() => {
    store.clearSelectedMatricule();
});

onBeforeRouteUpdate(() => {
    store.clearSelectedMatricule();
});

const fetchCompanyMatriculeByUuidIfNotInStore = async (uuid: string) => {
    const [isLoadingResponse, companyMatriculeResponse] = await fetchCompanyMatricule(uuid);
    isLoading.value = isLoadingResponse;
    if (companyMatriculeResponse) {
        store.setSelectedMatricule(companyMatriculeResponse);
    }
};

async function searchUsersWithoutMatriculeByCompany({ search, page, hasNextPage }) {
    if (companyMatricule.value && companyMatricule.value?.company.uuid) {
        const [totalCompaniesRecordsForDropDown, usersWithoutMatriculeList] = await getPaginatedUsersWithoutMatriculeByCompanyForDropDown(
            companyMatricule.value?.company.uuid,
            5,
            page,
            search,
            true,
        );
        
        // Always prepend the current store user to results if it exists
        let finalUsersList = [...usersWithoutMatriculeList];

        if (companyMatricule.value?.user && page === 1) {
            const defaultSelectedUser: IFormkitDropDown = {
                label: companyMatricule.value.user.fullname,
                value: companyMatricule.value.user.uuid,
            };

            // Check if the store user matches the search criteria
            const searchLower = search?.toLowerCase() || '';
            const userMatchesSearch = !searchLower || companyMatricule.value.user.fullname.toLowerCase().includes(searchLower);
            if (userMatchesSearch) {
                // Remove duplicates if the store user is already in the list
                finalUsersList = finalUsersList.filter(u => u.value !== defaultSelectedUser.value);
                // Add store user at the beginning
                finalUsersList.unshift(defaultSelectedUser);
            }
        }

        const hasItems = usersWithoutMatriculeList.length > 0;
        if (hasItems && page !== totalCompaniesRecordsForDropDown) {
            hasNextPage();
        }

        return finalUsersList;
    }

    return [];
}

const cannotEditAvailable = () => {
    return companyMatricule.value?.user;
};

const preventSpaceOnWriteText = (event: KeyboardEvent) => {
    if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault();
    }
};

const redirectToCompanyMatriculeList = (matricule: IUserMatriculeByCompany) => {
    store.setSelectedCompany({
        uuid: matricule.company.uuid,
        name: matricule.company.name,
    });
    
    router.push({
        name: 'userMatriculesByCompany',
        params: { uuid: matricule.company.uuid }
    });
};

const redirectToCompanyList = () => {
    store.clearAll();
    
    router.push({name: 'companyMatriculeWithUserList'});
};

const validateMatricule = (node) => {
    const value = node.value;
    if (!value) return true;

    const forbiddenChars = [' ', '.', ',', ';', '_'];
    for (const char of forbiddenChars) {
        if (value.includes(char)) {
            return false;
        }
    }

    return true;
};

const onSubmit = async () => {
    if (!companyMatricule.value) {
        return;
    }

    const companyMatriculeUuid = companyMatricule.value.uuid;
    const companyUuid = companyMatricule.value?.company.uuid;
    const companyName = companyMatricule.value?.company.name;
    try {
        const payload = {
            userMatricule: formData.value.userMatricule.trim(),
            available: formData.value.available,
            userUuid: formData.value.selectedUser || null,
        };

       await ApiService.patchWithoutSlug(`/admin/company-matricule/${companyMatriculeUuid}`, payload)
            .then((response) => {
                swal.success("Succès", "Les modifications ont bien été prises en compte");
                
                store.setSelectedCompany({
                    uuid: companyUuid,
                    name: companyName,
                });
                
                router.push({ name: 'userMatriculesByCompany' , params: { uuid: companyUuid }});
        });
    } catch (error: any) {
        console.error('Error when saving company matricule:', error);
        
        const apiResponse = error.response.data;
        let message = "Une Erreur est surevenue";
        if (apiResponse.errors.length) {
            const tempMessage = apiResponse.errors[0].message;
            if (
                tempMessage === 'Cet utilisateur a déjà un matricule.'
                || tempMessage === 'Ce matricule existe déjà pour cette entreprise.')
            {
                message = tempMessage;
            }
        }
        
        swal.error(message);
    }
};
</script>

<style scoped lang="scss">
    .user-details {
        margin-top: 30px;
        padding: 20px;
        background-color: #f8f9fa;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
    }
    
    .user-details h3 {
        margin: 0 0 16px 0;
        color: #2c3e50;
        font-size: 18px;
        font-weight: 600;
    }
    
    .user-info {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    
    .info-row {
        display: flex;
        align-items: center;
    }
    
    .info-label {
        font-weight: 600;
        color: #495057;
        min-width: 120px;
    }
    
    .info-value {
        color: #212529;
    }
    
    .no-user-assigned {
        margin-top: 30px;
        padding: 20px;
        background-color: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 8px;
        text-align: center;
    }
    
    .no-user-assigned p {
        margin: 0;
        color: #856404;
        font-weight: 500;
    }

    .error {
        text-align: center;
        padding: 40px;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .error p {
        color: #dc3545;
        font-size: 18px;
        margin-bottom: 20px;
    }
</style>